import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Filter, X, ChevronRight } from "lucide-react";
import { products, categories, colorPalettes, priceRanges } from "@/data/products";
import { farms } from "@/data/farms";
import { ProductCard } from "@/components/product/ProductCard";

export default function ProductsPage() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedColor, setSelectedColor] = useState("all");
  const [selectedPriceRange, setSelectedPriceRange] = useState("all");
  const [selectedFarm, setSelectedFarm] = useState("all");
  const [priceSlider, setPriceSlider] = useState([0, 300]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Category filter
      if (selectedCategory !== "all" && product.category !== selectedCategory) {
        return false;
      }

      // Color filter
      if (selectedColor !== "all" && product.colorPalette !== selectedColor) {
        return false;
      }

      // Price range filter
      if (selectedPriceRange !== "all") {
        const range = priceRanges.find((r) => r.id === selectedPriceRange);
        if (range && (product.price < range.min || product.price > range.max)) {
          return false;
        }
      }

      // Slider price filter
      if (product.price < priceSlider[0] || product.price > priceSlider[1]) {
        return false;
      }

      // Farm filter
      if (selectedFarm !== "all" && product.farmId !== selectedFarm) {
        return false;
      }

      return true;
    });
  }, [selectedCategory, selectedColor, selectedPriceRange, selectedFarm, priceSlider]);

  const clearFilters = () => {
    setSelectedCategory("all");
    setSelectedColor("all");
    setSelectedPriceRange("all");
    setSelectedFarm("all");
    setPriceSlider([0, 300]);
  };

  const hasActiveFilters =
    selectedCategory !== "all" ||
    selectedColor !== "all" ||
    selectedPriceRange !== "all" ||
    selectedFarm !== "all" ||
    priceSlider[0] > 0 ||
    priceSlider[1] < 300;

  // ✅ Important: render function, not an inline component
  const renderFilterContent = () => (
    <div className="space-y-6">
      {/* Category */}
      <div>
        <h3 className="font-medium text-foreground mb-3">Category</h3>
        <div className="space-y-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedCategory === category.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <Separator />

      {/* Color Palette */}
      <div>
        <h3 className="font-medium text-foreground mb-3">Color</h3>
        <div className="space-y-2">
          {colorPalettes.map((color) => (
            <button
              key={color.id}
              onClick={() => setSelectedColor(color.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedColor === color.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              {color.name}
            </button>
          ))}
        </div>
      </div>

      <Separator />

      {/* Price Range */}
      <div>
        <h3 className="font-medium text-foreground mb-3">Price Range</h3>
        <div className="px-2">
          <Slider
            value={priceSlider}
            onValueChange={setPriceSlider}
            min={0}
            max={300}
            step={10}
            className="mb-4"
          />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>€{priceSlider[0]}</span>
            <span>€{priceSlider[1]}</span>
          </div>
        </div>
      </div>

      <Separator />

      {/* Farm */}
      <div>
        <h3 className="font-medium text-foreground mb-3">Farm</h3>
        <div className="space-y-2">
          <button
            onClick={() => setSelectedFarm("all")}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              selectedFarm === "all"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`}
          >
            All Farms
          </button>
          {farms.map((farm) => (
            <button
              key={farm.id}
              onClick={() => setSelectedFarm(farm.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedFarm === farm.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              {farm.name}
            </button>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <>
          <Separator />
          <Button variant="outline" className="w-full" onClick={clearFilters}>
            <X className="h-4 w-4 mr-2" />
            Clear all filters
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div>
      {/* Hero */}
      <section className="bg-secondary/30 py-12 lg:py-16 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">Products</span>
          </nav>

          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
            Our Products
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            Discover premium wool products, each with a traceable story from Dutch farms.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-card rounded-xl border border-border p-6">
                <h2 className="font-display text-lg font-semibold text-foreground mb-6">
                  Filters
                </h2>
                {renderFilterContent()}
              </div>
            </aside>

            {/* Products Grid */}
            <div className="lg:col-span-3">
              {/* Mobile Filter Button & Sort */}
              <div className="flex items-center justify-between mb-6 lg:mb-8">
                <div className="flex items-center gap-3">
                  {/* Mobile Filter */}
                  <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="lg:hidden gap-2">
                        <Filter className="h-4 w-4" />
                        Filters
                        {hasActiveFilters && (
                          <Badge variant="secondary" className="ml-1">
                            Active
                          </Badge>
                        )}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 overflow-y-auto">
                      <SheetHeader>
                        <SheetTitle>Filters</SheetTitle>
                      </SheetHeader>
                      <div className="mt-6">{renderFilterContent()}</div>
                    </SheetContent>
                  </Sheet>

                  <p className="text-sm text-muted-foreground">
                    {filteredProducts.length} product{filteredProducts.length !== 1 ? "s" : ""}
                  </p>
                </div>

                {/* Sort */}
                <Select defaultValue="featured">
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Active Filters Pills */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedCategory !== "all" && (
                    <Badge variant="secondary" className="gap-1 pr-1">
                      {categories.find((c) => c.id === selectedCategory)?.name}
                      <button
                        onClick={() => setSelectedCategory("all")}
                        className="ml-1 hover:bg-muted rounded p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {selectedColor !== "all" && (
                    <Badge variant="secondary" className="gap-1 pr-1">
                      {colorPalettes.find((c) => c.id === selectedColor)?.name}
                      <button
                        onClick={() => setSelectedColor("all")}
                        className="ml-1 hover:bg-muted rounded p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {selectedFarm !== "all" && (
                    <Badge variant="secondary" className="gap-1 pr-1">
                      {farms.find((f) => f.id === selectedFarm)?.name}
                      <button
                        onClick={() => setSelectedFarm("all")}
                        className="ml-1 hover:bg-muted rounded p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                </div>
              )}

              {/* Products Grid */}
              {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-muted-foreground">No products match your filters.</p>
                  <Button variant="outline" className="mt-4" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
